/**
 * Decimal units, matching macOS, iOS, Android and every cloud storage UI:
 * 1 GB = 1,000,000,000 bytes. Dividing by 1024 and calling the result "GB" is
 * the usual way a file browser ends up disagreeing with Finder by 7%.
 */
export function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let value = bytes
  let unit = 0
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000
    unit += 1
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

export function parseBytes(input: string): number | null {
  const match = /^\s*(\d+(?:\.\d+)?)\s*(b|kb|mb|gb|tb|kib|mib|gib|tib)?\s*$/i.exec(input)
  if (!match) return null
  const scale: Record<string, number> = {
    b: 1,
    kb: 1e3,
    mb: 1e6,
    gb: 1e9,
    tb: 1e12,
    // Accept binary units too, for anyone who means exactly 2^30.
    kib: 1024,
    mib: 1024 ** 2,
    gib: 1024 ** 3,
    tib: 1024 ** 4,
  }
  return Math.round(Number(match[1]) * (scale[(match[2] ?? 'b').toLowerCase()] ?? 1))
}

const dateFormat = new Intl.DateTimeFormat('ko-KR', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

export function formatDate(value: number | string | null): string {
  if (value === null) return '-'
  return dateFormat.format(new Date(value))
}

/** Splits `/a/b/c` into crumbs with the cumulative path for each segment. */
export function breadcrumbs(path: string): { name: string; path: string }[] {
  const segments = path.split('/').filter(Boolean)
  const crumbs: { name: string; path: string }[] = []
  let cumulative = ''
  for (const segment of segments) {
    cumulative += `/${segment}`
    crumbs.push({ name: segment, path: cumulative })
  }
  return crumbs
}

export function joinPath(base: string, name: string): string {
  return base === '/' ? `/${name}` : `${base}/${name}`
}

/** `/a/b` 의 `b`. 루트에는 이름이 없으므로 빈 문자열이다. */
export function basename(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1)
}

export function parentPath(path: string): string {
  const index = path.lastIndexOf('/')
  return index <= 0 ? '/' : path.slice(0, index)
}
